import { useState, useEffect, useCallback, useRef } from 'react';

const LOCAL_STORAGE_KEY = 'vibrantcast_template_configs';

/**
 * Hook for managing template configurations with localStorage persistence
 * Each template can have custom font sizes and positions
 */
export const useTemplateConfig = () => {
  const [configs, setConfigs] = useState(() => {
    // Initialize from localStorage synchronously
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading template configs:', error);
    }
    return {};
  });
  
  // Keep a ref to avoid stale closures
  const configsRef = useRef(configs);
  configsRef.current = configs;

  /**
   * Get all saved template configurations
   */
  const getAllConfigs = useCallback(() => {
    return configsRef.current;
  }, []);

  /**
   * Get configuration for a specific template
   */
  const getConfig = useCallback((templateId) => {
    return configsRef.current[templateId] || null;
  }, []);

  /**
   * Save configuration for a specific template
   */
  const saveConfig = useCallback((templateId, config) => {
    const currentConfigs = configsRef.current;
    const newConfigs = { ...currentConfigs, [templateId]: config };
    setConfigs(newConfigs);
    configsRef.current = newConfigs;
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newConfigs));
      return true;
    } catch (error) {
      console.error('Error saving template config:', error);
      return false;
    }
  }, []);

  /**
   * Delete configuration for a specific template (reverts to default)
   */
  const deleteConfig = useCallback((templateId) => {
    const currentConfigs = configsRef.current;
    const newConfigs = { ...currentConfigs };
    delete newConfigs[templateId];
    setConfigs(newConfigs);
    configsRef.current = newConfigs;
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newConfigs));
      return true;
    } catch (error) {
      console.error('Error deleting template config:', error);
      return false;
    }
  }, []);

  /**
   * Export all configurations as JSON
   */
  const exportConfigs = useCallback(() => {
    return JSON.stringify(configsRef.current, null, 2);
  }, []);

  /**
   * Import configurations from JSON
   */
  const importConfigs = useCallback((jsonString) => {
    try {
      const importedConfigs = JSON.parse(jsonString);
      setConfigs(importedConfigs);
      configsRef.current = importedConfigs;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(importedConfigs));
      return true;
    } catch (error) {
      console.error('Error importing template configs:', error);
      return false;
    }
  }, []);

  return {
    getAllConfigs,
    getConfig,
    saveConfig,
    deleteConfig,
    exportConfigs,
    importConfigs,
  };
};
